import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Grid,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  Backdrop,
  useTheme,
} from "@mui/material";
import {
  BookmarkBorder,
  ContentCopy,
  Bolt,
  History,
  InfoOutlined,
  LibraryBooks,
  AutoAwesome,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

const citationStyles = ["APA 7", "MLA 9", "Chicago", "Harvard", "IEEE"];

export default function CitationGenerator(): JSX.Element {
  const [sourceData, setSourceData] = useState("");
  const [citation, setCitation] = useState("");
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState("APA 7");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleGenerate = async () => {
    if (!sourceData.trim()) return;
    setLoading(true);

    const prompt = `Generate a formal academic citation in ${style} style for the following source details or URL. If it's a URL, use your knowledge to provide the most accurate citation possible. Only return the final citation text: \n\n${sourceData}`;

    try {
      const response = await axios.post(`${API_BASE_URL}/gemini-generate`, {
        prompt: prompt,
        model: "gemini-flash-latest",
      }, { timeout: 90000 });

      if (response.data && response.data.result) {
        setCitation(response.data.result.trim());
      }
    } catch (error) {
      console.error("Citation generation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(citation);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Citation{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Tool
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Generate perfectly formatted citations for your research papers in
          seconds.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Input Panel */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              p: 3,
              minHeight: 400,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "white" : "text.primary",
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                Enter Source Details
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                placeholder="Paste a URL, DOI, or enter details (Author, Title, Date, etc.)... 
Example: https://nature.com/articles/s41586-021-03914-4 or Smith, J. (2023). AI in Science."
                value={sourceData}
                onChange={(e) => setSourceData(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                    borderRadius: "16px",
                    p: 2,
                    color: isDarkMode ? "white" : "text.primary",
                    fontSize: "14px",
                    lineHeight: 1.6,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                mt: "auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#4b5563" : "text.secondary",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Citation Style
                </Typography>
                <Box
                  sx={{
                    p: 0.5,
                    borderRadius: "10px",
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {citationStyles.slice(0, 3).map((s) => (
                    <Box
                      key={s}
                      onClick={() => setStyle(s)}
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: "7px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        transition: "all 0.2s",
                        bgcolor:
                          style === s
                            ? isDarkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.06)"
                            : "transparent",
                        color:
                          style === s
                            ? isDarkMode
                              ? "white"
                              : "primary.main"
                            : "#6b7280",
                        "&:hover": {
                          color: isDarkMode ? "white" : "primary.dark",
                        },
                      }}
                    >
                      {s}
                    </Box>
                  ))}
                  <FormControl
                    size="small"
                    variant="standard"
                    sx={{ minWidth: 100, ml: 1 }}
                  >
                    <Select
                      value={
                        citationStyles.includes(style)
                          ? citationStyles.indexOf(style) > 2
                            ? style
                            : "More"
                          : "More"
                      }
                      onChange={(e) =>
                        e.target.value !== "More" &&
                        setStyle(e.target.value as string)
                      }
                      disableUnderline
                      sx={{
                        color: "#6b7280",
                        fontSize: "12px",
                        fontWeight: 500,
                        pt: 0.5,
                      }}
                    >
                      <MenuItem value="More" disabled>
                        More styles
                      </MenuItem>
                      {citationStyles.slice(3).map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading || !sourceData.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Bolt />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  height: 44,
                  mt: 2,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Generate Citation
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Result Panel */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                minHeight: 250,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "text.secondary",
                  mb: 3,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <LibraryBooks fontSize="small" sx={{ color: "#3b82f6" }} />{" "}
                Formatted Citation ({style})
              </Typography>

              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
                ) : citation ? (
                  <Typography
                    sx={{
                      color: isDarkMode ? "white" : "text.primary",
                      fontSize: "15px",
                      lineHeight: 1.7,
                      textAlign: "center",
                      fontStyle: "italic",
                    }}
                  >
                    {citation}
                  </Typography>
                ) : (
                  <Box sx={{ opacity: 0.2, textAlign: "center" }}>
                    <BookmarkBorder
                      sx={{
                        fontSize: 48,
                        mb: 1,
                        color: isDarkMode ? "white" : "text.primary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: isDarkMode ? "white" : "text.primary" }}
                    >
                      Ready to format your reference
                    </Typography>
                  </Box>
                )}
              </Box>

              {citation && (
                <Button
                  fullWidth
                  startIcon={<ContentCopy />}
                  onClick={handleCopy}
                  sx={{
                    mt: 3,
                    color: isDarkMode ? "#9ca3af" : "text.secondary",
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                    textTransform: "none",
                    "&:hover": {
                      color: isDarkMode ? "white" : "primary.main",
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  Copy Citation
                </Button>
              )}
            </Paper>

            {/* Quick Tips */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "white" : "text.primary",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AutoAwesome fontSize="small" sx={{ color: "#3b82f6" }} /> AI
                Pro-Tip
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? "#6b7280" : "text.secondary",
                  lineHeight: 1.6,
                }}
              >
                You can paste a simple URL or a DOI number, and our AI will
                fetch the metadata to create a complete citation for you.
              </Typography>
            </Paper>

            <Box sx={{ px: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#4b5563" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <InfoOutlined fontSize="inherit" />
                Academic citations are generated following official style
                guides.
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 500, letterSpacing: "0.05em" }}
        >
          Generating Citation...
        </Typography>
      </Backdrop>
    </Box>
  );
}
